// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import HubRoundedIcon from '@mui/icons-material/HubRounded';

// material ui
import { Chip, Stack, Typography } from '@mui/material';

// feature-components
import CopyableIdField from '@/features/devices/components/shared/CopyableIdField';
import DetailHeaderCard from '@/features/devices/components/shared/DetailHeaderCard';
import DetailInfoField from '@/features/devices/components/shared/DetailInfoField';
import useCopyToClipboard from '@/features/devices/components/shared/hooks/useCopyToClipboard';

type IntegrationLike = {
  Name?: string;
  Description?: string;
  Status?: string;
  status?: string;
  ImplementationStatus?: string;
  AssociatedUnitIds?: Array<string | number>;
  UnitIds?: Array<string | number>;
  Units?: Array<{ UnitId?: string | number; id?: string | number }>;
  IntegrationId?: string | number;
  EforsyningIntegrationId?: string | number;
  Id?: string | number;
};

const getIntegrationId = (integration: IntegrationLike | undefined) =>
  integration?.EforsyningIntegrationId ??
  integration?.IntegrationId ??
  integration?.Id;

const getStatusValue = (integration: IntegrationLike | undefined) =>
  integration?.Status ??
  integration?.ImplementationStatus ??
  integration?.status ??
  '';

const getAssociatedUnitIds = (integration: IntegrationLike | undefined) => {
  if (Array.isArray(integration?.AssociatedUnitIds)) {
    return integration.AssociatedUnitIds;
  }
  if (Array.isArray(integration?.UnitIds)) {
    return integration.UnitIds;
  }
  if (Array.isArray(integration?.Units)) {
    return integration.Units.map((unit) => unit?.UnitId ?? unit?.id).filter(
      (id): id is string | number => id != null,
    );
  }

  return [];
};

const EforsyningIntegrationDetailHeader = ({
  integration,
}: {
  integration: IntegrationLike | undefined;
}) => {
  const { t } = useTranslation(['eforsyning-integration', 'common']);
  const copyToClipboard = useCopyToClipboard();

  const integrationId = getIntegrationId(integration);
  const associatedUnitIds = getAssociatedUnitIds(integration);
  const statusValue = String(getStatusValue(integration) ?? '').trim();

  const handleCopyIntegrationId = () =>
    copyToClipboard(
      integrationId ? String(integrationId) : '',
      t('integrationIdCopied', {
        defaultValue: 'Integration ID copied',
      }),
      t('integrationIdCopyFailed', {
        defaultValue: 'Failed to copy Integration ID',
      }),
    );

  return (
    <DetailHeaderCard
      title={
        integration?.Name ||
        t('common.notAvailable', {
          ns: 'common',
          defaultValue: 'Not Available',
        })
      }
      subheader={integration?.Description}
      avatar={<HubRoundedIcon fontSize="medium" />}
    >
      <DetailInfoField
        label={t('integrationId', {
          defaultValue: 'Integration ID',
        })}
      >
        <CopyableIdField
          value={integrationId ? String(integrationId) : ''}
          notAvailableText={t('common.notAvailable', {
            ns: 'common',
            defaultValue: 'Not Available',
          })}
          onCopy={handleCopyIntegrationId}
        />
      </DetailInfoField>

      <DetailInfoField
        label={t('common.status', { ns: 'common', defaultValue: 'Status' })}
      >
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {statusValue ||
            t('common.notAvailable', {
              ns: 'common',
              defaultValue: 'Not Available',
            })}
        </Typography>
      </DetailInfoField>

      <DetailInfoField
        label={t('associatedUnits', {
          defaultValue: 'Associated Unit Id(s)',
        })}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {associatedUnitIds.length > 0 ? (
            associatedUnitIds.map((unitId) => (
              <Chip key={String(unitId)} label={String(unitId)} size="small" />
            ))
          ) : (
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {t('common.notAvailable', {
                ns: 'common',
                defaultValue: 'Not Available',
              })}
            </Typography>
          )}
        </Stack>
      </DetailInfoField>
    </DetailHeaderCard>
  );
};

export default EforsyningIntegrationDetailHeader;
